import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import Icons from "@/components/Icons";
import LoadingOverlay from "@/components/LoadingOverlay";
import PaymentSettings from "@/components/PaymentSettings";
import type { DiceDreamsCheckoutProps } from "@/interfaces/index";
import { openCustomerPortal } from "@/lib/customer-portal-utils";
import { performDeeplinkRedirect } from "@/lib/deeplink-utils";
import { useStyleTheme } from "@/hooks/useStyleTheme";
import { useAnalytics } from "@/hooks/useAnalytics";

const DiceDreamsCheckout = ({ 
  gameData, 
  savedCards = [], 
  onShowSettings, 
  showSettings = false, 
  onHideSettings,
  onRefreshCards,
  isInSubPage = false,
  onBackToMainPage
}: DiceDreamsCheckoutProps) => {
  const theme = useStyleTheme(gameData);
  const { track } = useAnalytics();
  const [isCustomerPortalLoading, setIsCustomerPortalLoading] = useState(false);
  const [isBackButtonEnabled, setIsBackButtonEnabled] = useState(false);
  
  // Function to check if we're in portrait mode (YOUR LOGIC)
  const isPortraitMode = () => {
    return window.innerWidth < window.innerHeight || window.innerWidth < 768;
  };

  // STAGING LOGIC: Configuration hierarchy for back button
  const getBackButtonEnabled = useCallback((): boolean => {
    try {
      // 1. Check URL parameter first (highest priority)
      const url = new URL(window.location.href);
      const backButtonParam = url.searchParams.get('back_button_enabled');
      if (backButtonParam !== null) {
        return backButtonParam === 'true' || backButtonParam === '1';
      }
      
      // 2. Check game config (second priority)
      if (gameData && 'back_button_enabled' in gameData) {
        return Boolean(gameData.back_button_enabled);
      }
      
      // 3. Default to false (lowest priority)
      return false;
    } catch (error) {
      console.warn('Error parsing back button configuration:', error);
      // Fallback to game config or default
      if (gameData && 'back_button_enabled' in gameData) {
        return Boolean(gameData.back_button_enabled);
      }
      return false;
    }
  }, [gameData]);

  useEffect(() => {
    const enabled = getBackButtonEnabled();
    setIsBackButtonEnabled(enabled);
  }, [getBackButtonEnabled]);

  // STAGING LOGIC: Handle page visibility changes for bfcache
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      // Check if page was restored from bfcache
      if (event.persisted && isCustomerPortalLoading) {
        console.log('Page restored from bfcache, resetting customer portal loading state');
        setIsCustomerPortalLoading(false);
      }
    };

    const handleVisibilityChange = () => {
      // Reset loading state when page becomes visible again
      if (document.visibilityState === 'visible' && isCustomerPortalLoading) {
        console.log('Page became visible, resetting customer portal loading state');
        setIsCustomerPortalLoading(false);
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isCustomerPortalLoading]);

  const handleSettingsClick = () => {
    try {
      track('click_in_checkout', { cta_name: 'settings' });
    } catch {
      // Ignore tracking errors
    }
    if (onShowSettings) {
      onShowSettings();
    } else {
      // Fallback to old behavior if no callback provided
      handleLegacySettingsClick();
    }
  };

  const handleLegacySettingsClick = async () => {
    await openCustomerPortal({
      gameData,
      onLoadingStart: () => setIsCustomerPortalLoading(true),
    });
  };

  // YOUR LOGIC: For portrait mode, render settings component directly here
  // For landscape mode, settings are handled in CheckoutForm's right panel
  if (showSettings && gameData && isPortraitMode()) {
    return (
      <PaymentSettings
        gameData={gameData}
        savedCards={savedCards}
        onBack={onHideSettings || (() => {})}
        onRefreshCards={onRefreshCards}
      />
    );
  }

  // STAGING LOGIC: Handle back button / title click
  const handleTitleOrBackButtonClick = async () => {
    // If user is in a sub-page, go back to main checkout page
    if (isInSubPage && onBackToMainPage) {
      onBackToMainPage();
      return;
    }
    
    // Otherwise, redirect to game deeplink
    await performDeeplinkRedirect({
      gameData,
      source: 'any'
    });
  };

  return (
    <>
      <LoadingOverlay 
        isVisible={isCustomerPortalLoading} 
        message="Opening payment settings..." 
        isAbsolute={false}
        theme={theme}
      />
      <section className="w-full px-5 flex justify-between items-center relative min-h-[60px] mt-[-8px]">
        <div className="flex items-center gap-3">
          {isBackButtonEnabled && (
            <Button
              className="mb-[2px] flex items-center justify-center"
              size="box"
              aria-label="Go back to game"
              onClick={handleTitleOrBackButtonClick}
            >
              <div style={{ transform: 'rotate(180deg)' }}>
                <Icons type="arrow" size={7} />
              </div>
            </Button>
          )}
          <p 
            className="text-[#A2998B] font-['Poppins'] text-[14px] font-normal leading-[normal] text-edge-cap leading-trim-both cursor-pointer"
            onClick={handleTitleOrBackButtonClick}
          >
            {gameData?.checkout_title}
          </p>
        </div>
        <Button
          className="w-[22px] h-[22px]"
          size="box"
          aria-label="Open checkout settings"
          onClick={handleSettingsClick}
        >
        <Icons type="settings" size={19} />
      </Button>
    </section>
    </>
  );
};

export default React.memo(DiceDreamsCheckout);
